import { FC, MouseEventHandler, useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { observer } from "mobx-react-lite";

export interface SearchResultPdfChipItem {
  id: string;
  label: string;
  fileSizeBytes: number | null;
  showSyncedStatus: boolean;
  onClick: () => void;
}

export interface SearchResultImageThumbnailItem {
  id: string;
  src: string;
  alt?: string;
  onClick: () => void;
}

export enum SearchResultAttachmentMediaOrder {
  PdfFirst = "pdf-first",
  ImageFirst = "image-first",
}

interface SearchResultPdfChipsProps {
  chips: SearchResultPdfChipItem[];
  imageThumbnails?: SearchResultImageThumbnailItem[];
  mediaOrder?: SearchResultAttachmentMediaOrder;
}

export const SearchResultPdfChips: FC<SearchResultPdfChipsProps> = observer(
  ({ chips, imageThumbnails = [], mediaOrder = SearchResultAttachmentMediaOrder.PdfFirst }) => {
    const chipsContainerRef = useRef<HTMLDivElement | null>(null);
    const moreChipMeasureRef = useRef<HTMLSpanElement | null>(null);
    const chipWidthsRef = useRef(new Map<string, number>());
    const [visibleChipCount, setVisibleChipCount] = useState(chips.length);
    const chipsMeasureKey = chips
      .map(chip => `${chip.id}:${chip.label}:${chip.fileSizeBytes ?? ""}:${chip.showSyncedStatus}`)
      .join("|");

    const setChipRef = useCallback(
      (chipId: string) => (element: HTMLButtonElement | null) => {
        if (element) {
          chipWidthsRef.current.set(chipId, element.offsetWidth);
        }
      },
      []
    );

    const updateVisibleChipCount = useCallback(() => {
      const container = chipsContainerRef.current;
      if (!container) {
        setVisibleChipCount(chips.length);
        return;
      }

      const availableWidth = container.clientWidth;
      const style = window.getComputedStyle(container);
      const resolvedGap = Number.parseFloat(style.columnGap || style.gap || "0");
      const gapPx = Number.isFinite(resolvedGap) ? resolvedGap : 0;
      const moreChipWidth = moreChipMeasureRef.current?.offsetWidth ?? 0;

      let nextVisibleChipCount = 0;
      let usedWidth = 0;

      for (const chip of chips) {
        const chipWidth = chipWidthsRef.current.get(chip.id);
        if (!chipWidth) {
          setVisibleChipCount(chips.length);
          return;
        }

        const gapBeforeChip = nextVisibleChipCount > 0 ? gapPx : 0;
        const remainingChipCount = chips.length - (nextVisibleChipCount + 1);
        const reservedMoreChipWidth = remainingChipCount > 0 ? gapPx + moreChipWidth : 0;
        const requiredWidth = usedWidth + gapBeforeChip + chipWidth + reservedMoreChipWidth;

        if (requiredWidth > availableWidth) {
          break;
        }

        usedWidth += gapBeforeChip + chipWidth;
        nextVisibleChipCount += 1;
      }

      setVisibleChipCount(nextVisibleChipCount);
    }, [chips]);

    useLayoutEffect(() => {
      setVisibleChipCount(chips.length);
    }, [chips.length, chipsMeasureKey]);

    useLayoutEffect(() => {
      const container = chipsContainerRef.current;
      if (!container) {
        return;
      }

      const animationFrame = requestAnimationFrame(updateVisibleChipCount);
      const resizeObserver = new ResizeObserver(() => {
        updateVisibleChipCount();
      });
      resizeObserver.observe(container);

      return () => {
        cancelAnimationFrame(animationFrame);
        resizeObserver.disconnect();
      };
    }, [chips, updateVisibleChipCount]);

    const hasPdfChips = chips.length > 0;
    const hasImageThumbnails = imageThumbnails.length > 0;

    if (!hasPdfChips && !hasImageThumbnails) {
      return null;
    }

    const visibleChips = chips.slice(0, visibleChipCount);
    const hiddenChipCount = chips.length - visibleChips.length;
    const orderedSections =
      mediaOrder === SearchResultAttachmentMediaOrder.ImageFirst
        ? (["images", "pdf"] as const)
        : (["pdf", "images"] as const);

    return (
      <Container data-test-id="search-result-attachments">
        {orderedSections.map(section => {
          if (section === "pdf" && hasPdfChips) {
            return (
              <ChipsContainer
                key="pdf"
                ref={chipsContainerRef}
                data-test-id="search-result-pdf-chips-container"
                data-disable-note-preview-on-hover="true"
              >
                {visibleChips.map(chip => (
                  <SearchResultPdfChip key={chip.id} {...chip} chipRef={setChipRef(chip.id)} />
                ))}
                {hiddenChipCount > 0 && (
                  <StyledMoreText
                    as="span"
                    variant={MDSTextVariant.TextSm}
                    weight={MDSTextWeight.Regular}
                    color={MDSTextColor.Secondary}
                  >
                    {`+${hiddenChipCount} more`}
                  </StyledMoreText>
                )}
                <MoreTextMeasureWrapper ref={moreChipMeasureRef}>
                  <StyledMoreText
                    as="span"
                    variant={MDSTextVariant.TextSm}
                    weight={MDSTextWeight.Regular}
                    color={MDSTextColor.Secondary}
                  >
                    {`+${chips.length} more`}
                  </StyledMoreText>
                </MoreTextMeasureWrapper>
              </ChipsContainer>
            );
          }

          if (section === "images" && hasImageThumbnails) {
            return (
              <ImageThumbnailsContainer
                key="images"
                data-test-id="search-result-image-thumbnails-container"
                data-disable-note-preview-on-hover="true"
              >
                {imageThumbnails.map(imageThumbnail => (
                  <ImageThumbnail key={imageThumbnail.id} imageThumbnail={imageThumbnail} />
                ))}
              </ImageThumbnailsContainer>
            );
          }

          return null;
        })}
      </Container>
    );
  }
);

interface SearchResultPdfChipProps extends SearchResultPdfChipItem {
  chipRef?: (element: HTMLButtonElement | null) => void;
}

const SearchResultPdfChip: FC<SearchResultPdfChipProps> = ({
  label,
  fileSizeBytes,
  showSyncedStatus,
  onClick,
  chipRef,
}) => {
  const fileSize = formatFileSize(fileSizeBytes);

  const handleClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  };

  return (
    <ChipButton ref={chipRef} type="button" onClick={handleClick}>
      <ChipLabel>
        <FileIcon>
          <MDSIcon kind={MDSIconKind.Pdf} size={14} color={MDSIconColor.Secondary} />
        </FileIcon>
        <FileName>{label}</FileName>
      </ChipLabel>
      <ChipRight>
        {showSyncedStatus || fileSize ? (
          <>
            {showSyncedStatus && (
              <MDSIcon
                kind={MDSIconKind.CheckCircleSolid}
                size={14}
                color={MDSIconColor.Secondary}
              />
            )}
            {fileSize && <FileSize>{fileSize}</FileSize>}
          </>
        ) : (
          <ChipStatusPlaceholder />
        )}
      </ChipRight>
    </ChipButton>
  );
};

const ImageThumbnail: FC<{ imageThumbnail: SearchResultImageThumbnailItem }> = ({
  imageThumbnail,
}) => {
  const handleClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    event.stopPropagation();
    imageThumbnail.onClick();
  };

  return (
    <ImageThumbnailButton
      type="button"
      onClick={handleClick}
      aria-label={imageThumbnail.alt || "Open image attachment"}
    >
      <ImageThumbnailImage
        src={imageThumbnail.src}
        alt={imageThumbnail.alt || "Image attachment"}
        loading="lazy"
      />
    </ImageThumbnailButton>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const ChipsContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const ImageThumbnailsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const ImageThumbnailButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  line-height: 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: ${({ theme }) => theme.colors.border.surface};
    }
  }
`;

const ImageThumbnailImage = styled.img`
  display: block;
  width: 80px;
  height: 80px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
`;

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-width: 160px;
  max-width: 480px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid transparent;
  background-color: ${({ theme }) => theme.colors.background.button.default.default};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => `${theme.spacing["1"]} ${theme.spacing["2"]}`};
  cursor: pointer;
  overflow: hidden;
  user-select: none;
  box-sizing: border-box;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${({ theme }) => theme.colors.background.button.default.hover};
    }
  }
`;

const ChipLabel = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  min-width: 0;
  width: 100%;
  overflow: hidden;
`;

const FileIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: ${({ theme }) => theme.spacing["1"]};
  flex-shrink: 0;
`;

const FileName = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 400;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
`;

const ChipRight = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing["1"]};
`;

const FileSize = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 14px;
  line-height: 20px;
  text-wrap: nowrap;
`;

const ChipStatusPlaceholder = styled.span``;

const StyledMoreText = styled(MDSText)`
  display: inline-block;
  flex-shrink: 0;
  white-space: nowrap;
`;

const MoreTextMeasureWrapper = styled.span`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
`;

const formatFileSize = (fileSizeBytes: number | null): string | null => {
  if (typeof fileSizeBytes !== "number" || fileSizeBytes < 0) {
    return null;
  }

  const KILOBYTE = 1024;
  const MEGABYTE = KILOBYTE * 1024;
  const GIGABYTE = MEGABYTE * 1024;

  if (fileSizeBytes < KILOBYTE) {
    return `${fileSizeBytes} B`;
  }

  if (fileSizeBytes < MEGABYTE) {
    return `${(fileSizeBytes / KILOBYTE).toFixed(0)} KB`;
  }

  if (fileSizeBytes < GIGABYTE) {
    return `${(fileSizeBytes / MEGABYTE).toFixed(0)} MB`;
  }

  return `${(fileSizeBytes / GIGABYTE).toFixed(0)} GB`;
};
